'use client';

import React, { useEffect, useState } from 'react';

import { isUserLoggedInOptimistic } from '@editor/stores/userStore/utils';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { EventCatcherScope } from '@repo/event-processor';
import { clearAllTokens, getProfileToken } from '@repo/transport/token';
import { Button } from '@repo/ui-kit/components/core/Button';
import { P2 } from '@repo/ui-kit/components/core/Typography';

import { FlexColumn } from 'styles/layouts';

import FullScreenLoader from './FullScreenLoader';

type LoggedInFullScreenLoaderProps = {
  opacity?: number;
  isDark?: boolean;
};

const LoggedInFullScreenLoader: React.FC<LoggedInFullScreenLoaderProps> = (
  props
) => {
  const [showReloadMessage, setShowReloadMessage] = useState(false);
  const user = useUserStore(userStoreSelector.user);
  const isLoading = useUserStore((state) => state.isLoading);
  const isUserMaybeLoggedIn = isUserLoggedInOptimistic();
  const profileToken = getProfileToken();

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowReloadMessage(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleReload = () => {
    // Clear all tokens before reloading
    clearAllTokens();

    window.location.reload();
  };

  return (
    <EventCatcherScope
      isLoggedIn
      feature="loggedInFullScreenLoader"
      scopeParameters={{
        user: user ? JSON.stringify(user) : undefined,
        hasToken: profileToken ? 'yes' : 'no',
        isLoading: isLoading ? 'yes' : 'no',
        isUserMaybeLoggedIn: isUserMaybeLoggedIn ? 'yes' : 'no',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent:
          typeof window !== 'undefined'
            ? window.navigator.userAgent
            : undefined,
      }}
    >
      <FullScreenLoader {...props}>
        {showReloadMessage && (
          <FlexColumn gap="var(--spacing-8)">
            <P2>This seems to be taking longer than expected</P2>

            <Button
              eventOptions={{
                shouldTrack: {
                  click: true,
                  show: true,
                },
              }}
              name="reloadSiteButton"
              onClick={handleReload}
            >
              <Button.Label>Reload Site</Button.Label>
            </Button>
          </FlexColumn>
        )}
      </FullScreenLoader>
    </EventCatcherScope>
  );
};

export default LoggedInFullScreenLoader;
